import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef, type ChangeEvent } from 'react';

import { CheckSquareOutlined, BorderOutlined, SearchOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Space, Button, message, Spin, Checkbox, Input, Tag, Empty, Alert } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useTranslation } from 'react-i18next';

interface MerchantRow {
  id: string;
  name: string;
  activated?: boolean;
  [key: string]: unknown;
}

interface MerchantApiListResponse {
  success: boolean;
  data?: MerchantRow[];
  has_more?: boolean;
}

interface MerchantApiIdsResponse {
  success: boolean;
  data?: string[];
  message?: string;
}

interface MerchantApiUpdateResponse {
  success: boolean;
  message?: string;
  [key: string]: unknown;
}

interface OperatorServiceLike {
  updateMerchants?: (operatorId: string | null, ids: string[]) => Promise<MerchantApiUpdateResponse>;
  [key: string]: unknown;
}

interface MerchantServiceLike {
  listAllWarehouseMerchants: (params?: { page?: number; pageSize?: number; keyword?: string; scope?: string; warehouseId?: string }) => Promise<MerchantApiListResponse>;
  getWarehouseMerchantIds: (params?: { keyword?: string; scope?: string; warehouseId?: string }) => Promise<MerchantApiIdsResponse>;
  [key: string]: unknown;
}

export interface MerchantPermissionManagerHandle {
  save: () => Promise<string[] | void>;
  saving: boolean;
}

/**
 * Merchant 權限管理組件（Shared Component）
 * 使用 server-side pagination + keyword 搜尋
 *
 * @param operatorService - operator API service
 * @param merchantService - merchant API service (需提供 listAllWarehouseMerchants, getWarehouseMerchantIds)
 * @param operatorMerchants - 當前 Operator 已關聯的 Merchants [{id, name}]
 * @param operatorId - Operator ID
 * @param canEdit - 是否可編輯
 * @param mode - 'edit' 為既有編輯模式（save 直接打 API）；
 *   'invite' 為邀請表單嵌入模式（save 不打 API，僅回傳當前選取陣列），selectedKeys 變動會透過 onChange 通知 parent
 * @param onChange - mode='invite' 時，selectedKeys 變動的 callback，參數為已選 merchant id 陣列
 */
interface MerchantPermissionManagerProps {
  operatorService: OperatorServiceLike;
  merchantService: MerchantServiceLike;
  operatorMerchants?: MerchantRow[];
  operatorId: string | null;
  canEdit?: boolean;
  mode?: 'edit' | 'invite';
  onChange?: (ids: string[]) => void;
  // 指定要列哪個 warehouse 的 merchants（invite modal 中 inviter 可選擇任一 warehouse）。
  // 不傳則 BE fallback 到 current_warehouse（header）。
  warehouseId?: string;
}

const MerchantPermissionManager = forwardRef<MerchantPermissionManagerHandle, MerchantPermissionManagerProps>(({
  operatorService,
  merchantService,
  operatorMerchants = [],
  operatorId,
  canEdit = true,
  mode = 'edit',
  onChange,
  warehouseId,
}, ref) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [merchants, setMerchants] = useState<MerchantRow[]>([]); // 當前頁的 merchants
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [allMerchantIds, setAllMerchantIds] = useState<string[]>([]); // 用於 orphan 偵測和總數
  const [orphanedMerchants, setOrphanedMerchants] = useState<MerchantRow[]>([]);
  const [pageLoading, setPageLoading] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 清除 debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // invite 模式下對 BE 加上 scope=invitable，請求只回 inviter 個人 SAM 範圍，
  // 避免 FE 顯示「能勾但送出 403 merchant_exceeds_granter」的清單。
  // edit / 其他用途不傳 scope，維持「warehouse 全 list」行為。
  const fetchScope = mode === 'invite' ? 'invitable' : undefined;

  // 初始載入 + warehouseId 變動時重新載入：取得第一頁 + 全部 merchant IDs
  // invite modal 中 inviter 改選 warehouse 時，merchant 清單必須跟著切換。
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [listRes, idsRes] = await Promise.all([
          merchantService.listAllWarehouseMerchants({ page: 1, scope: fetchScope, warehouseId }),
          merchantService.getWarehouseMerchantIds({ scope: fetchScope, warehouseId }),
        ]);

        if (listRes.success && listRes.data) {
          setMerchants(listRes.data);
          setHasMore(listRes.has_more || false);
          setCurrentPage(1);
        }
        if (idsRes.success && idsRes.data) {
          setAllMerchantIds(idsRes.data);
          setTotalCount(idsRes.data.length);
        }
      } catch (error) {
        console.error('Failed to fetch merchants:', error);
        message.error(t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [warehouseId]);

  // 當 allMerchantIds 或 operatorMerchants 變更時，初始化 selectedKeys 和 orphanedMerchants
  // 內容相同則保留舊 reference，避免 mode='invite' 下 parent 因 .map() 每次 render
  // 新建 operatorMerchants 陣列而與 onChange 形成 setState ↔ effect 無限循環
  useEffect(() => {
    if (allMerchantIds.length === 0 && !loading) return;

    if (operatorMerchants && operatorMerchants.length > 0) {
      const warehouseIdSet = new Set(allMerchantIds);
      const validIds = operatorMerchants
        .filter(m => warehouseIdSet.has(m.id))
        .map(m => m.id);
      const orphaned = operatorMerchants.filter(m => !warehouseIdSet.has(m.id));

      setSelectedKeys(prev => {
        if (prev.size === validIds.length && validIds.every(id => prev.has(id))) {
          return prev;
        }
        return new Set(validIds);
      });
      setOrphanedMerchants(prev => {
        if (prev.length === orphaned.length && orphaned.every((o, i) => o.id === prev[i]?.id)) {
          return prev;
        }
        return orphaned;
      });
    } else {
      setSelectedKeys(prev => (prev.size === 0 ? prev : new Set()));
      setOrphanedMerchants(prev => (prev.length === 0 ? prev : []));
    }
  }, [operatorMerchants, allMerchantIds]);

  // mode='invite' 時，selectedKeys 變動透過 onChange 通知 parent
  // 注意：不能用 useEffect 監聽 selectedKeys 推給 parent。過去用 effect 推 onChange 會跟
  // L132 那條「prop → selectedKeys」effect 形成雙向同步迴圈：
  //   user click → setSelectedKeys → effect fires → onChange → parent setState
  //     → parent re-render（即使其他 state 變動也一樣）→ operatorMerchants 新 ref → L132 fires
  //     → 比對內容若有暫態不一致就 setSelectedKeys → effect 又 fires → 死循環
  // → React `Maximum update depth exceeded` fatal、checkbox 自己亂跳。
  // 改成在 user-action handler 內 inline 呼叫 onChange（單向：user action → parent），
  // L132 仍負責處理 prop 變動（編輯模式 server fetch 初始化），但不再回推。
  const notifyParent = useCallback((nextSet: Set<string>) => {
    if (mode === 'invite' && typeof onChange === 'function') {
      onChange(Array.from(nextSet));
    }
  }, [mode, onChange]);

  // 取得指定頁面的 merchants
  const fetchPage = useCallback(async (page: number, keyword?: string) => {
    try {
      setPageLoading(true);
      const params: { page: number; keyword?: string; scope?: string; warehouseId?: string } = { page };
      if (keyword) params.keyword = keyword;
      if (fetchScope) params.scope = fetchScope;
      if (warehouseId) params.warehouseId = warehouseId;
      const data = await merchantService.listAllWarehouseMerchants(params);

      if (data.success && data.data) {
        setMerchants(data.data);
        setHasMore(data.has_more || false);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Failed to fetch merchants page:', error);
      message.error(t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
    } finally {
      setPageLoading(false);
    }
  }, [merchantService, t, fetchScope, warehouseId]);

  // 搜尋 debounce
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value || '';
    setSearchText(value);

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchPage(1, value || undefined);
    }, 300);
  }, [fetchPage]);

  // 注意：先算 nextSet → setSelectedKeys(nextSet) → notifyParent(nextSet) 兩步分離。
  // 不把 notifyParent 寫在 setSelectedKeys(prev => ...) 的 updater 內：
  // React 規範要求 updater 為 pure function，StrictMode（dev）會 double-invoke updater
  // 來偵測非純副作用，導致 onChange 在 dev 環境每次 click 被呼叫兩次；同時 concurrent
  // rendering 也可能在 commit 前重跑 updater，造成 parent state 與 child 預期不一致。
  const handleCheckboxChange = (merchantId: string, checked: boolean) => {
    const newSet = new Set(selectedKeys);
    if (checked) {
      newSet.add(merchantId);
    } else {
      newSet.delete(merchantId);
    }
    setSelectedKeys(newSet);
    notifyParent(newSet);
  };

  // 全選當頁
  const handleSelectPage = () => {
    const newSet = new Set(selectedKeys);
    merchants.forEach(m => newSet.add(m.id));
    setSelectedKeys(newSet);
    notifyParent(newSet);
  };

  // 取消全選當頁
  const handleDeselectPage = () => {
    const newSet = new Set(selectedKeys);
    merchants.forEach(m => newSet.delete(m.id));
    setSelectedKeys(newSet);
    notifyParent(newSet);
  };

  // 全選所有（含搜尋條件）
  const handleSelectAll = async () => {
    try {
      const params: { keyword?: string; scope?: string; warehouseId?: string } = {};
      if (searchText) params.keyword = searchText;
      if (fetchScope) params.scope = fetchScope;
      if (warehouseId) params.warehouseId = warehouseId;
      const data = await merchantService.getWarehouseMerchantIds(params);
      if (data.success && data.data) {
        const newSet = new Set(selectedKeys);
        data.data.forEach(id => newSet.add(id));
        setSelectedKeys(newSet);
        notifyParent(newSet);
      } else {
        // BE 回 success:false：使用者按了全選但實際沒生效，不通知會以為「按了沒事」
        message.error(data.message || t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch all merchant IDs:', error);
      message.error(t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
    }
  };

  // 取消全選所有（含搜尋條件）
  const handleDeselectAll = async () => {
    if (searchText) {
      // 有搜尋條件時，只取消符合條件的
      try {
        const data = await merchantService.getWarehouseMerchantIds({ keyword: searchText, scope: fetchScope, warehouseId });
        if (data.success && data.data) {
          const newSet = new Set(selectedKeys);
          data.data.forEach(id => newSet.delete(id));
          setSelectedKeys(newSet);
          notifyParent(newSet);
        } else {
          // BE 回 success:false：使用者按了取消全選但實際沒生效，要通知避免 silent 失敗
          message.error(data.message || t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
        }
      } catch (error) {
        console.error('Failed to fetch all merchant IDs:', error);
        message.error(t('operator.accounts.merchant_permissions.error.fetch_merchants_failed'));
      }
    } else {
      const emptySet = new Set<string>();
      setSelectedKeys(emptySet);
      notifyParent(emptySet);
    }
  };

  // 換頁
  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchPage(currentPage - 1, searchText || undefined);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      fetchPage(currentPage + 1, searchText || undefined);
    }
  };

  const handleSave = async (): Promise<string[] | void> => {
    if (mode === 'invite') {
      // invite 模式：save 由外部表單統一處理，不打 API
      return Array.from(selectedKeys);
    }
    if (!canEdit) {
      message.warning(t('operator.accounts.merchant_permissions.error.no_permission'));
      return;
    }

    try {
      setSaving(true);
      const data = await operatorService.updateMerchants!(operatorId, Array.from(selectedKeys));

      if (data.success) {
        message.success(t('operator.accounts.merchant_permissions.save_success'));
      } else {
        throw new Error(data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Failed to update merchants:', error);
      message.error(t('operator.accounts.merchant_permissions.error.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selectedKeys.size;

  // Expose save to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
    saving,
  }));

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: '#666', margin: 0 }}>
          {t('operator.accounts.merchant_permissions.description')}
        </p>
      </div>

      {/* 警告：有不在當前 warehouse 的舊授權 */}
      {orphanedMerchants.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
          message={t('operator.accounts.merchant_permissions.orphaned_warning', {
            count: orphanedMerchants.length
          })}
          description={
            <div style={{ marginTop: '8px' }}>
              {orphanedMerchants.map(merchant => (
                <Tag key={merchant.id} color="orange" style={{ marginBottom: '4px' }}>
                  {merchant.name}
                </Tag>
              ))}
            </div>
          }
        />
      )}

      {/* 工具列 */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px',
        background: '#fafafa',
        borderRadius: '6px'
      }}>
        <Input
          placeholder={t('common.button.search')}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={searchText}
          onChange={handleSearchChange}
          style={{ width: 240 }}
          allowClear
        />

        {canEdit && (
          <Space>
            <Button
              size="small"
              icon={<CheckSquareOutlined />}
              onClick={handleSelectAll}
            >
              {t('common.button.select_all')}
            </Button>
            <Button
              size="small"
              icon={<BorderOutlined />}
              onClick={handleDeselectAll}
            >
              {t('common.button.deselect_all')}
            </Button>
          </Space>
        )}

        <div style={{ marginLeft: 'auto' }}>
          <Tag color="blue">
            {t('operator.accounts.merchant_permissions.selected_count', { count: selectedCount })} / {totalCount}
          </Tag>
        </div>
      </div>

      {/* 貨主列表 */}
      <Spin spinning={pageLoading}>
        <div style={{
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          minHeight: '200px',
        }}>
          {merchants.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchText ? t('common.no_results') : t('common.no_data')}
              style={{ padding: '40px 0' }}
            />
          ) : (
            merchants.map((merchant, index) => {
              const isSelected = selectedKeys.has(merchant.id);
              return (
                <div
                  key={merchant.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px 16px',
                    borderBottom: index < merchants.length - 1 ? '1px solid #f0f0f0' : 'none',
                    background: isSelected ? '#e6f7ff' : 'transparent',
                    cursor: canEdit ? 'pointer' : 'default',
                    transition: 'background 0.2s'
                  }}
                  onClick={() => canEdit && handleCheckboxChange(merchant.id, !isSelected)}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={!canEdit}
                    onChange={(e: CheckboxChangeEvent) => {
                      e.stopPropagation?.();
                      handleCheckboxChange(merchant.id, e.target.checked);
                    }}
                    style={{ marginRight: '12px' }}
                  />
                  <span style={{
                    flex: 1,
                    fontWeight: isSelected ? 500 : 400
                  }}>
                    {merchant.name}
                  </span>
                  {!merchant.activated && (
                    <Tag color="default" style={{ marginLeft: '8px' }}>
                      {t('common.status.inactive')}
                    </Tag>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Spin>

      {/* 分頁控制 */}
      {merchants.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '12px',
          padding: '8px 0'
        }}>
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={currentPage <= 1}
            onClick={handlePrevPage}
          >
            {t('operator.accounts.merchant_permissions.prev_page')}
          </Button>
          <span style={{ color: '#666', fontSize: '13px' }}>
            {t('operator.accounts.merchant_permissions.page_info', { page: currentPage })}
          </span>
          <Button
            size="small"
            disabled={!hasMore}
            onClick={handleNextPage}
          >
            {t('operator.accounts.merchant_permissions.next_page')} <RightOutlined />
          </Button>
        </div>
      )}
    </div>
  );
});

export default MerchantPermissionManager;
